import { Modal, Button } from 'antd';
import { Form } from '@/components';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  GenerateTasksReportArgs,
  useGenerateTasksReportMutation,
} from '@/redux';
import { FormGenerateTasksReport } from './FormGenerateTasksReport';
import { FileExcelOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import i18next from 'i18next';
import { TaskStatus } from '@/core';
import { parseResponsiblePersonValue } from '../Tasks.Common';

const schema = Yup.object().shape({
  startDate: Yup.date().nullable(),
  endDate: Yup.date()
    .when('startDate', (startDate, yup) => {
      if (startDate)
        return yup.min(startDate, {
          key: 'reports_end_date_must_be_after_start_date',
        });
    })
    .nullable(),
  clientIds: Yup.array()
    .of(Yup.string().nullable())
    .min(1, i18next.t('errors.mixed.required'))
    .required(),
  communityIds: Yup.array()
    .of(Yup.string().nullable())
    .min(1, i18next.t('errors.mixed.required'))
    .required(),
  projectIds: Yup.array().of(Yup.string().nullable()),
  homeIds: Yup.array().of(Yup.string().nullable()),
  responsiblePersons: Yup.array().of(Yup.string().nullable()),
  statuses: Yup.array().of(Yup.mixed<TaskStatus>().nullable()),
});

const INITIAL_VALUE: GenerateTasksReportArgs = {
  startDate: null!,
  endDate: null!,
  clientIds: [],
  communityIds: [],
  projectIds: [],
  homeIds: [],
  responsiblePersons: [],
  statuses: [],
};

interface Props {
  onClose: () => any;
}

const useSubmit = (props: Props) => {
  const { onClose } = props;
  const [generate] = useGenerateTasksReportMutation();

  return (args: GenerateTasksReportArgs) => {
    generate({
      ...args,
      projectIds: isEmpty(args.projectIds) ? undefined : args.projectIds,
      homeIds: isEmpty(args.homeIds) ? undefined : args.homeIds,
      responsiblePersons: isEmpty(args.responsiblePersonValues)
        ? undefined
        : args.responsiblePersonValues?.map((x) =>
            parseResponsiblePersonValue(x),
          ),
    })
      .unwrap()
      .then((uri) => {
        window.open(
          '/api/report/inline?src=' + encodeURIComponent(uri),
          '_blank',
        );

        onClose();
      });
  };
};

export function GenerateTasksReportModal(props: Props) {
  const { onClose: onCancel } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'reports.tasksReport',
  });

  const handleSubmit = useSubmit(props);

  return (
    <Modal
      title={t('title')}
      visible
      onCancel={onCancel}
      footer={[
        <Button key="back" type="default" onClick={onCancel}>
          {t('cancel')}
        </Button>,
        <Form.Submit
          uid="reports-tasksReport"
          key="submit"
          type="primary"
          icon={<FileExcelOutlined />}
        >
          {t('runReport')}
        </Form.Submit>,
      ]}
    >
      <Form.Formik<GenerateTasksReportArgs>
        uid="reports-tasksReport"
        i18n="reports.tasksReport"
        initialValues={{ ...INITIAL_VALUE }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        <FormGenerateTasksReport />
      </Form.Formik>
    </Modal>
  );
}
